import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext(null);
const DELETED_ACCOUNT_ERROR_CODE = 'auth/account-deleted';

const createDeletedAccountError = () => {
    const error = new Error('This account has been deleted.');
    error.code = DELETED_ACCOUNT_ERROR_CODE;
    return error;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const docRef = doc(db, 'members', firebaseUser.uid);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const profile = docSnap.data();
                        if (profile?.isDeleted === true) {
                            await signOut(auth);
                            setUser(null);
                            setUserProfile(null);
                            setAuthLoading(false);
                            return;
                        }
                        setUser(firebaseUser);
                        setUserProfile(profile);
                    } else {
                        setUser(firebaseUser);
                        setUserProfile(null);
                    }
                } catch (err) {
                    console.error('Error fetching user profile:', err);
                    setUser(firebaseUser);
                    setUserProfile(null);
                }
            } else {
                setUser(null);
                setUserProfile(null);
            }
            setAuthLoading(false);
        });
        return unsubscribe;
    }, []);

    const refreshUserProfile = async (targetUser = user) => {
        if (!targetUser?.uid) return null;
        try {
            const docRef = doc(db, 'members', targetUser.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const nextProfile = docSnap.data();
                setUserProfile(nextProfile);
                return nextProfile;
            }
        } catch (err) {
            console.error('Error refreshing user profile:', err);
        }
        return null;
    };

    const login = async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        try {
            const docRef = doc(db, 'members', result.user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const profile = docSnap.data();
                if (profile?.isDeleted === true) {
                    await signOut(auth);
                    setUser(null);
                    setUserProfile(null);
                    throw createDeletedAccountError();
                }
                setUserProfile(profile);
            } else {
                setUserProfile(null);
            }
        } catch (err) {
            if (err.code === DELETED_ACCOUNT_ERROR_CODE) {
                throw err;
            }
            console.error('Error fetching profile after login:', err);
        }
        return result;
    };

    const logout = async () => {
        await signOut(auth);
        setUser(null);
        setUserProfile(null);
    };

    const updateMemberProfile = async (updates) => {
        if (!user?.uid) throw new Error('You must be logged in to update your profile.');

        await setDoc(doc(db, 'members', user.uid), {
            ...updates,
            updatedAt: serverTimestamp(),
        }, { merge: true });

        if (typeof updates?.name === 'string' && updates.name.trim() && updates.name !== user.displayName) {
            await updateProfile(user, { displayName: updates.name.trim() });
        }

        await refreshUserProfile(user);
    };

    const registerMember = async ({
        name, email, password, phoneNumber, country, city,
        dob, gender, membershipType, billingInterval,
        motivation, interests, skills,
        expertise,
        education,
        currentPosition,
        organization,
        yearsOfExperience,
        contribution,
        linkedIn,
        website,
        publications,
        availability,
        requestedMembershipType,
        advisorApplicationStatus,
        paymentStatus,
        status,
        stripeCustomerId,
        stripeSubscriptionId,
        stripeSubscriptionStatus,
        membershipActivatedAt,
    }) => {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(credential.user, { displayName: name });

        const profileData = {
            uid: credential.user.uid,
            name,
            email: email.toLowerCase().trim(),
            phoneNumber: phoneNumber || null,
            country,
            city,
            dob: dob || null,
            gender: gender || null,
            membershipType: membershipType || 'general',
            requestedMembershipType: requestedMembershipType || null,
            advisorApplicationStatus: advisorApplicationStatus || null,
            billingInterval: billingInterval || 'free',
            motivation: motivation || null,
            interests: interests || [],
            skills: skills || [],
            expertise: expertise || null,
            education: education || null,
            currentPosition: currentPosition || null,
            organization: organization || null,
            yearsOfExperience: yearsOfExperience || null,
            contribution: contribution || null,
            linkedIn: linkedIn || null,
            website: website || null,
            publications: publications || null,
            availability: availability || null,
            paymentStatus: paymentStatus || (billingInterval && billingInterval !== 'free' ? 'pending' : 'free'),
            status: status || (billingInterval && billingInterval !== 'free' ? 'pending' : 'active'),
            stripeCustomerId: stripeCustomerId || null,
            stripeSubscriptionId: stripeSubscriptionId || null,
            stripeSubscriptionStatus: stripeSubscriptionStatus || null,
            membershipActivatedAt: membershipActivatedAt || null,
            createdAt: serverTimestamp(),
        };

        await setDoc(doc(db, 'members', credential.user.uid), profileData);
        setUser(credential.user);
        setUserProfile(profileData);
        return credential.user;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                userProfile,
                authLoading,
                login,
                logout,
                registerMember,
                refreshUserProfile,
                updateMemberProfile,
                setUserProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
